import { useCallback, useMemo, useRef } from 'react'
import { Animated, Easing, Platform } from 'react-native'
import { NAVIGATION_BACK, transitions } from '@adalo/constants'
import { createStack, push, peek } from 'utils/stacks'
import { parseURL } from 'utils/notifications'
import { FLOAT_TRANSITIONS } from './useTransitionAnimations'

export const useNavigationHandlers = ({
  routeStack,
  baseOffset,
  navigationInProgress,
  dispatch,
  currentViewOffset,
  getTiming,
  notify,
  resetRandomNumbers,
  handlePasswordReset,
  setupRoutes,
  initialComponentId,
}) => {
  // Create refs for current values
  const routeStackRef = useRef(routeStack)
  const baseOffsetRef = useRef(baseOffset)

  // Update refs when values change
  useMemo(() => {
    routeStackRef.current = routeStack
    baseOffsetRef.current = baseOffset
  }, [routeStack, baseOffset])

  const getRoute = useCallback(route => {
    const newRoute = {
      params: {},
      transition: transitions.PUSH,
      ...route,
    }
    return newRoute
  }, [])

  const postPop = useCallback(() => {
    dispatch({ type: 'POP_ROUTE' })
  }, [dispatch])

  const navigateBack = useCallback(() => {
    const currentRouteStack = routeStackRef.current
    const currentBaseOffset = baseOffsetRef.current

    if (!currentRouteStack) {
      return
    }

    const currentRoute = peek(currentRouteStack)
    if (!currentRoute) {
      return
    }

    if (currentRouteStack.length <= 1) {
      return
    }

    const { transition } = currentRoute

    resetRandomNumbers()

    const timing = Animated.timing(currentViewOffset, {
      toValue: currentBaseOffset,
      easing: Easing.bezier(0.3, 1.06, 0.65, 1),
      duration: getTiming(transition),
      delay: 0,
      useNativeDriver: true,
    })

    const previousRoute = peek(currentRouteStack, 1)
    notify(previousRoute)

    dispatch({ type: 'SET_NAVIGATION_PROGRESS', payload: { inProgress: true } })

    timing.start(() => {
      postPop()
      dispatch({
        type: 'SET_NAVIGATION_PROGRESS',
        payload: { inProgress: false },
      })
    })
  }, [
    currentViewOffset,
    resetRandomNumbers,
    getTiming,
    notify,
    postPop,
    dispatch,
  ])

  const goBack = useCallback(
    (...args) => {
      const currentRouteStack = routeStackRef.current
      if (currentRouteStack && currentRouteStack.length > 1) {
        navigateBack()
        return true
      }
      return false
    },
    [navigateBack]
  )

  const goBackWrapped = useCallback(() => {
    const currentRouteStack = routeStackRef.current

    // For SPAs, prioritize internal navigation if we have multiple routes
    if (currentRouteStack && currentRouteStack.length > 1) {
      navigateBack()
    } else if (window.history.length > 1) {
      window.history.go(-1)
    }
  }, [navigateBack])

  const navigate = useCallback(
    action => {
      if (!action) {
        return
      }

      // Reset animation value if navigation is already in progress
      if (navigationInProgress) {
        currentViewOffset.setValue(0)
        dispatch({
          type: 'SET_NAVIGATION_PROGRESS',
          payload: { inProgress: false },
        })
      }

      const currentRouteStack = routeStackRef.current
      if (!currentRouteStack) {
        // Try to setup routes immediately
        setupRoutes()
        // Queue the navigation for when routes are ready
        dispatch({
          type: 'ADD_PENDING_NAVIGATION',
          payload: { navigation: action },
        })
        return
      }

      const currentRoute = peek(currentRouteStack)
      if (!currentRoute) {
        return
      }

      const { transition } = action

      if (
        action.target === NAVIGATION_BACK ||
        action.type === NAVIGATION_BACK
      ) {
        if (Platform.OS === 'web') {
          goBackWrapped()
        } else {
          navigateBack()
        }
      } else {
        const currentBaseOffset = baseOffsetRef.current
        const timing = Animated.timing(currentViewOffset, {
          toValue: currentBaseOffset + 2,
          easing: Easing.bezier(0.3, 1.06, 0.65, 1),
          duration: getTiming(transition),
          delay: 0,
          useNativeDriver: true,
        })

        const newParams = action.params
        const oldParams = currentRoute.params
        const params = { ...oldParams, ...newParams }

        // Update state and start animation in the correct order for float transitions
        if (FLOAT_TRANSITIONS.includes(transition)) {
          // Start animation first
          currentViewOffset.setValue(currentBaseOffset)
          timing.start(() => {
            dispatch({
              type: 'SET_NAVIGATION_PROGRESS',
              payload: { inProgress: false },
            })
            if (handlePasswordReset) {
              handlePasswordReset()
            }
          })
          // Then update state
          notify(action)
          dispatch({
            type: 'PUSH_ROUTE',
            payload: {
              route: { ...action, params },
            },
          })
        } else {
          // For non-float transitions, keep existing behavior
          notify(action)
          dispatch({
            type: 'PUSH_ROUTE',
            payload: {
              route: { ...action, params },
            },
          })
          timing.start(() => {
            dispatch({
              type: 'SET_NAVIGATION_PROGRESS',
              payload: { inProgress: false },
            })
            if (handlePasswordReset) {
              handlePasswordReset()
            }
          })
        }
      }
    },
    [
      currentViewOffset,
      getTiming,
      goBackWrapped,
      navigateBack,
      notify,
      handlePasswordReset,
      setupRoutes,
      dispatch,
      navigationInProgress,
    ]
  )

  const setURL = useCallback(
    url => {
      if (!url) {
        return
      }

      let { screen, prevScreen, params } = parseURL(
        typeof url === 'object' ? url.url : url
      )

      if (!screen) {
        return
      }

      if (!prevScreen) {
        prevScreen = initialComponentId
      }

      let newRouteStack = createStack({
        target: prevScreen,
        params: {},
        transition: null,
      })

      newRouteStack = push(newRouteStack, {
        target: screen,
        params,
        transition: transitions.PUSH,
      })

      dispatch({
        type: 'RESET_STATE',
        payload: {
          routeStack: newRouteStack,
        },
      })
      currentViewOffset.setValue(0)
    },
    [initialComponentId, currentViewOffset, dispatch]
  )

  const resetOffset = useCallback(
    newBaseOffset => {
      dispatch({
        type: 'RESET_STATE',
        payload: {
          routeStack,
          baseOffset: newBaseOffset,
        },
      })
    },
    [routeStack, dispatch]
  )

  return {
    navigate,
    goBack,
    goBackWrapped,
    navigateBack,
    setURL,
    resetOffset,
    getRoute,
    postPop,
  }
}
